import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { MenuModule } from 'primeng/menu';
import { AiResultFileFormat, aiResultToFile } from '../../core/utils/ai-result-file.util';

/**
 * Copiar o resultado da IA ou baixá-lo como arquivo (md, txt, pdf e, havendo JSON, json).
 * Usado no resultado do `ai-runner-dialog` (modal do chamado) e no detalhe da execução
 * (chamado 0000579137). Os arquivos saem de `aiResultToFile`, o mesmo da ação "Salvar como anexo".
 */
@Component({
  selector: 'ai-result-export',
  standalone: true,
  imports: [MenuModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['../../shared/ai-form.scss'],
  styles: [':host { display: contents; }'],
  template: `
    <button
      type="button"
      class="ai-btn ai-btn--sm ai-btn--ghost"
      [disabled]="!content()"
      (click)="copy()">
      <i [class]="copied() ? 'pi pi-check' : 'pi pi-copy'" aria-hidden="true"></i>
      {{ copied() ? 'Copiado' : 'Copiar' }}
    </button>
    <button
      type="button"
      class="ai-btn ai-btn--sm ai-btn--ghost"
      [disabled]="!content() || downloading()"
      aria-haspopup="true"
      (click)="menu.toggle($event)">
      <i [class]="downloading() ? 'pi pi-spin pi-spinner' : 'pi pi-download'" aria-hidden="true"></i>
      Baixar
    </button>
    <p-menu #menu [model]="items()" [popup]="true" appendTo="body" />
    @if (error()) {
      <small class="ai-runner__error">{{ error() }}</small>
    }
  `,
})
export class AiResultExportComponent {
  /** Texto do resultado (markdown). */
  readonly content = input<string | null | undefined>('');
  /** JSON do resultado, quando houver: libera o download .json. */
  readonly json = input<unknown>(null);
  /** Nome do arquivo sem extensão. */
  readonly fileName = input('resultado-ia');
  /** Título no topo do PDF. */
  readonly title = input<string | undefined>(undefined);

  readonly copied = signal(false);
  readonly downloading = signal(false);
  readonly error = signal<string | null>(null);

  readonly items = computed<MenuItem[]>(() => {
    const items: MenuItem[] = [
      { label: 'Markdown (.md)', icon: 'pi pi-file', command: () => this.download('md') },
      { label: 'Texto (.txt)', icon: 'pi pi-align-left', command: () => this.download('txt') },
      { label: 'PDF (.pdf)', icon: 'pi pi-file-pdf', command: () => this.download('pdf') },
    ];
    if (this.json() !== null && this.json() !== undefined) {
      items.push({ label: 'JSON (.json)', icon: 'pi pi-code', command: () => this.download('json') });
    }
    return items;
  });

  async copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.content() ?? '');
      this.copied.set(true);
      setTimeout(() => this.copied.set(false), 2000);
    } catch {
      this.error.set('Não foi possível copiar (permissão da área de transferência).');
    }
  }

  async download(format: AiResultFileFormat): Promise<void> {
    this.downloading.set(true);
    this.error.set(null);
    try {
      const content = format === 'json' ? this.json() : (this.content() ?? '');
      const file = await aiResultToFile(content, this.fileName(), format, this.title());
      const url = URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch {
      this.error.set('Não foi possível gerar o arquivo.');
    } finally {
      this.downloading.set(false);
    }
  }
}
